import { supabaseRestRequest } from './familyService';
import { logger } from '../utils/logger';

const CURIOSITY_RULE_ID = '1163cc3a-ccf2-4dba-bc15-092010e5299b';
const BRT_TIMEZONE = 'America/Sao_Paulo';

const getTodayDateInBRT = (): string => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
};

export type MarkFactAsReadReason = 'already_marked_today' | 'unknown_error';

export interface MarkFactAsReadResult {
  success: boolean;
  reason?: MarkFactAsReadReason;
  message?: string;
}

export interface HomeopathyFact {
  id: string;
  title: string;
  content: string;
  category: 'general' | 'history' | 'principles' | 'remedies' | 'research' | 'practice' | 'curiosity';
  difficulty: 'basic' | 'intermediate' | 'advanced';
  tags: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserFactRead {
  id: string;
  userId: string;
  factId: string;
  readAt: string;
}

class FactService {
  private static instance: FactService;

  private constructor() {}

  public static getInstance(): FactService {
    if (!FactService.instance) {
      FactService.instance = new FactService();
    }
    return FactService.instance;
  }

  async getFactsReadSince(userId: string, sinceDate: string): Promise<string[]> {
    try {
      console.log(' [FactService] Buscando leituras recentes', JSON.stringify({ userId, sinceDate }));
      const data = await supabaseRestRequest(
        `user_fact_reads?user_id=eq.${userId}&read_date=gte.${sinceDate}&select=fact_id`,
        'GET'
      );

      if (!Array.isArray(data)) {
        return [];
      }

      const factIds = data
        .map((item: any) => item?.fact_id)
        .filter((id: unknown): id is string => typeof id === 'string');

      console.log(
        ' [FactService] Leituras recentes encontradas',
        JSON.stringify({ userId, sinceDate, factIds })
      );

      return factIds;
    } catch (error: any) {
      console.error(' [FactService] Erro ao buscar curiosidades lidas recentemente:', error);
      return [];
    }
  }

  async getTodayReadFact(userId: string, todayDate: string): Promise<string | null> {
    try {
      const data = await supabaseRestRequest(
        `user_fact_reads?user_id=eq.${userId}&read_date=eq.${todayDate}&select=fact_id&order=read_at.desc&limit=1`,
        'GET'
      );

      if (Array.isArray(data) && data.length > 0 && typeof data[0]?.fact_id === 'string') {
        return data[0].fact_id;
      }

      return null;
    } catch (error) {
      console.error(' [FactService] Erro ao buscar curiosidade lida hoje:', error);
      return null;
    }
  }

  async getAllActiveFacts(): Promise<HomeopathyFact[]> {
    console.log(' [FactService] Buscando curiosidades ativas');

    try {
      const data = await supabaseRestRequest(
        'homeopathy_facts?active=eq.true&order=created_at.asc&select=*',
        'GET'
      );

      const facts = Array.isArray(data) ? data : [];
      console.log(' [FactService] Curiosidades ativas carregadas', JSON.stringify({ count: facts.length }));
      return this.formatFacts(facts);
    } catch (error: any) {
      console.error(' [FactService] Erro ao buscar curiosidades ativas:', error);
      return [];
    }
  }

  async selectRandomFact(excludedIds: string[] = []): Promise<HomeopathyFact | null> {
    try {
      const activeFacts = await this.getAllActiveFacts();
      if (activeFacts.length === 0) {
        return null;
      }

      const exclusionSet = new Set(excludedIds.map((id) => id.trim()));
      const availableFacts = activeFacts.filter((fact) => !exclusionSet.has(fact.id.trim()));
      console.log(
        '🎯 [FactService] Candidatos para sorteio',
        JSON.stringify({
          totalActive: activeFacts.length,
          excludedCount: exclusionSet.size,
          availableCount: availableFacts.length
          // firstAvailable removido por segurança (não expor IDs)
        })
      );

      const pool = availableFacts.length > 0 ? availableFacts : activeFacts;

      const randomIndex = Math.floor(Math.random() * pool.length);
      return pool[randomIndex];
    } catch (error) {
      console.error('❌ [FactService] Erro ao selecionar curiosidade aleatória:', error);
      return null;
    }
  }

  async getAllReadFactIds(userId: string): Promise<string[]> {
    try {
      const data = await supabaseRestRequest(
        `user_fact_reads?user_id=eq.${userId}&select=fact_id`,
        'GET'
      );

      if (!Array.isArray(data)) {
        return [];
      }

      const normalizedIds = data
        .map((item: any) => item?.fact_id)
        .filter((id: unknown): id is string => typeof id === 'string')
        .map((id) => id.trim());

      return Array.from(new Set(normalizedIds));
    } catch (error) {
      console.error('❌ [FactService] Erro ao buscar todas as curiosidades lidas:', error);
      return [];
    }
  }

  async selectFactForUser(userId: string): Promise<HomeopathyFact | null> {
    try {
      const now = new Date();
      const dateInBRT = new Date(now.getTime() - 3 * 60 * 60 * 1000);
      const utcMidnight = new Date(dateInBRT.getUTCFullYear(), dateInBRT.getUTCMonth(), dateInBRT.getUTCDate());
      const todayDate = utcMidnight.toISOString().split('T')[0];
      
      // 1. Verifica se já marcou como lida HOJE
      const todayReadFactId = await this.getTodayReadFact(userId, todayDate);
      if (todayReadFactId) {
        console.log('✅ [FactService] Usuário já leu hoje, retornando mesma curiosidade:', todayReadFactId);
        return await this.getFactById(todayReadFactId);
      }

      // 2. Busca TODAS as curiosidades já lidas (não só dos últimos dias)
      const allReadIds = await this.getAllReadFactIds(userId);
      logger.debug('Curiosidades já lidas:', { count: allReadIds.length });

      // 3. Sorteia uma nova excluindo as já lidas
      const fact = await this.selectRandomFact(allReadIds);

      if (fact) {
        logger.sensitive('Curiosidade sorteada', { factId: fact.id });
        return fact;
      }

      // 4. Se todas foram lidas, sorteia qualquer uma (reset do ciclo)
      console.log('🔄 [FactService] Todas curiosidades já foram lidas, resetando ciclo');
      return await this.selectRandomFact();
    } catch (error) {
      console.error('❌ [FactService] Erro ao selecionar curiosidade para usuário:', error);
      return null;
    }
  }

  async getAllFacts(): Promise<HomeopathyFact[]> {
    try {
      const data = await supabaseRestRequest('homeopathy_facts?order=created_at.desc&select=*', 'GET');
      return this.formatFacts(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error(' [FactService] Erro ao buscar curiosidades:', error);
      return [];
    }
  }

  async getFactsByCategory(category: HomeopathyFact['category']): Promise<HomeopathyFact[]> {
    try {
      const data = await supabaseRestRequest(
        `homeopathy_facts?category=eq.${category}&active=eq.true&order=created_at.desc&select=*`,
        'GET'
      );

      return this.formatFacts(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error(' [FactService] Erro ao buscar curiosidades por categoria:', error);
      return [];
    }
  }

  async addFact(fact: Omit<HomeopathyFact, 'id' | 'createdAt' | 'updatedAt'>): Promise<HomeopathyFact | null> {
    try {
      const data = await supabaseRestRequest('homeopathy_facts?select=*', 'POST', {
        title: fact.title,
        content: fact.content,
        category: fact.category,
        difficulty: fact.difficulty,
        tags: fact.tags,
        active: fact.active,
      });

      return this.formatFact(Array.isArray(data) ? data[0] : data);
    } catch (error: any) {
      console.error(' [FactService] Erro ao adicionar curiosidade:', error);
      throw new Error(`Erro ao adicionar curiosidade: ${error.message}`);
    }
  }

  async updateFact(
    id: string,
    updates: Partial<Omit<HomeopathyFact, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<HomeopathyFact | null> {
    try {
      const payload: Record<string, any> = {};

      if (updates.title !== undefined) payload.title = updates.title;
      if (updates.content !== undefined) payload.content = updates.content;
      if (updates.category !== undefined) payload.category = updates.category;
      if (updates.difficulty !== undefined) payload.difficulty = updates.difficulty;
      if (updates.tags !== undefined) payload.tags = updates.tags;
      if (updates.active !== undefined) payload.active = updates.active;

      const data = await supabaseRestRequest(
        `homeopathy_facts?id=eq.${id}&select=*`,
        'PATCH',
        payload
      );

      return this.formatFact(Array.isArray(data) ? data[0] : data);
    } catch (error: any) {
      console.error(' [FactService] Erro ao atualizar curiosidade:', error);
      throw new Error(`Erro ao atualizar curiosidade: ${error.message}`);
    }
  }

  async deleteFact(id: string): Promise<void> {
    try {
      await supabaseRestRequest(`homeopathy_facts?id=eq.${id}`, 'DELETE');
    } catch (error: any) {
      console.error(' [FactService] Erro ao deletar curiosidade:', error);
      throw new Error(`Erro ao deletar curiosidade: ${error.message}`);
    }
  }

  async markFactAsRead(
    factId: string,
    userId: string,
    pointsAwarded: boolean = false
  ): Promise<MarkFactAsReadResult> {
    try {
      const payload: Record<string, any> = {
        user_id: userId,
        fact_id: factId,
        points_awarded: pointsAwarded,
        read_date: getTodayDateInBRT(),
      };

      if (pointsAwarded) {
        payload.rule_id = CURIOSITY_RULE_ID;
      }

      await supabaseRestRequest('user_fact_reads', 'POST', payload);
      return { success: true };
    } catch (error: any) {
      const message = error?.message || '';
      const normalizedMessage = message.toLowerCase();
      const duplicateConstraint =
        normalizedMessage.includes('duplicate key value') ||
        normalizedMessage.includes('user_fact_reads_user_fact_day_key') ||
        normalizedMessage.includes('409');

      if (duplicateConstraint) {
        return { success: false, reason: 'already_marked_today', message };
      }

      return { success: false, reason: 'unknown_error', message };
    }
  }

  async hasUserReadFact(factId: string, userId: string): Promise<boolean> {
    try {
      const data = await supabaseRestRequest(
        `user_fact_reads?user_id=eq.${userId}&fact_id=eq.${factId}&select=*`,
        'GET'
      );

      return Array.isArray(data) && data.length > 0;
    } catch (error: any) {
      console.error(' [FactService] Erro ao verificar leitura:', error);
      return false;
    }
  }

  async getFactById(factId: string): Promise<HomeopathyFact | null> {
    try {
      const data = await supabaseRestRequest(
        `homeopathy_facts?id=eq.${factId}&select=*`,
        'GET'
      );

      if (Array.isArray(data) && data.length > 0) {
        return this.formatFact(data[0]);
      }

      if (data && !Array.isArray(data)) {
        return this.formatFact(data);
      }

      return null;
    } catch (error: any) {
      console.error(' [FactService] Erro ao buscar curiosidade por ID:', error);
      return null;
    }
  }

  async getUnreadFactForUser(userId: string): Promise<HomeopathyFact | null> {
    try {
      const allFacts = await this.getAllActiveFacts();
      if (allFacts.length === 0) {
        return null;
      }

      const readFacts = await supabaseRestRequest(
        `user_fact_reads?user_id=eq.${userId}&select=fact_id`,
        'GET'
      );

      const readFactIds = Array.isArray(readFacts)
        ? readFacts
            .map((item: any) => item?.fact_id)
            .filter((id: unknown): id is string => typeof id === 'string')
        : [];

      const unreadFacts = allFacts.filter((fact) => !readFactIds.includes(fact.id));

      const pool = unreadFacts.length > 0 ? unreadFacts : allFacts;
      const randomIndex = Math.floor(Math.random() * pool.length);
      return pool[randomIndex];
    } catch (error: any) {
      console.error(' [FactService] Erro ao buscar curiosidade não lida:', error);
      return null;
    }
  }

  private formatFact(data: any): HomeopathyFact {
    return {
      id: data.id,
      title: data.title,
      content: data.content,
      category: data.category,
      difficulty: data.difficulty,
      tags: Array.isArray(data.tags) ? data.tags : [],
      active: Boolean(data.active),
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  private formatFacts(data: any[]): HomeopathyFact[] {
    if (!Array.isArray(data)) {
      return [];
    }

    return data
      .map((item: any) => this.formatFact(item))
      .filter((fact): fact is HomeopathyFact => Boolean(fact));
  }
}

export default FactService;
